import { FC, ReactNode, useState } from "react";
import cx from "classnames";
import useMediaQuery from "@/custom_hooks/mediaQuery";
import logoLight from "@/assets/icons/logo_light.svg";
import Spotify from "@/assets/icons/spotify";
import Podcast from "@/assets/icons/podcast";
import Instagram from "@/assets/icons/instagram";
import Youtube from "@/assets/icons/youtube";
import cofunded_eu from "@/assets/icons/EN_Co-funded.svg";
import footerBg from "@/assets/images/footer/footer.png";
import { NavLink, useLocation } from "react-router-dom";
import Facebook from "@/assets/icons/facebook";
import ROUTES from "@/data/Routes";
import { useFormik } from "formik";
import { toFormikValidate } from "zod-formik-adapter";
import { z } from "zod";
import Input from "@/components/UI/Input";
import useNewslettersSubscription from "@/custom_hooks/useNewslettersSubcription";
import useHomeHeroQuery from "@/custom_hooks/useHomeHeroQuery";
import { Button } from "@/components/UI/Buttons/Button";
import { ActionLink } from "../UI/Buttons";
import {
  HOME_HERO_KLAVIYO_CONFIG,
  isValidHomeHeroVariant,
} from "@/data/homeHeroVariants";

const schema = z.object({
  email: z
    .string()
    .email("Neteisingas el. pašto adresas")
    .min(1, "Būtinas laukelis"),
});

const FOOTER_BANNER_TITLE_BY_QUERY: Record<string, string> = {
  strategija: "Jūsų verslas turi planą. Laikas jį turėti ir sveikatai.",
  nuovargis: "Pavargote nuo nuovargio? Laikas sužinoti priežastį.",
  rezultatai: "Nustok optimizuoti aklai. Pradėk nuo pilno vaizdo.",
};

const DefaultFooterBannerTitle = () => (
  <>
    Tai ne klinika, <br className="md:hidden" /> tai judėjimas
  </>
);

const Footer: FC = () => {
  const isMobile = useMediaQuery(768);
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  const showTopImage =
    location.pathname === ROUTES.HOME ||
    location.pathname === ROUTES.MEMBERSHIPS;

  const heroQuery = useHomeHeroQuery();
  const bannerTitle =
    location.pathname === ROUTES.HOME &&
    FOOTER_BANNER_TITLE_BY_QUERY[heroQuery] ? (
      FOOTER_BANNER_TITLE_BY_QUERY[heroQuery]
    ) : (
      <DefaultFooterBannerTitle />
    );

  const newsletterKlaviyoMetricType =
    location.pathname === ROUTES.HOME && isValidHomeHeroVariant(heroQuery)
      ? HOME_HERO_KLAVIYO_CONFIG[heroQuery].metricType
      : undefined;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");

  const { subscribe, ConfirmationCheckbox } = useNewslettersSubscription(
    setErrorMsg,
    setSuccessMsg,
    setIsLoading,
    newsletterKlaviyoMetricType,
  );

  const formik = useFormik({
    initialValues: { email: "" },
    validate: toFormikValidate(schema),
    onSubmit: async (values: { email: string }) => {
      await subscribe(values.email);
      if (!errorMsg) {
        formik.resetForm();
      }
    },
  });

  const isEmailEmpty = formik.values.email.trim().length === 0;

  return (
    <footer className="flex w-full flex-col">
      {/* TOP IMAGE SECTION */}
      {showTopImage && (
        <div
          className="relative hidden h-[500px] w-full bg-cover bg-center px-4 md:flex md:aspect-[640/291] md:h-auto"
          style={{ backgroundImage: `url(${footerBg})` }}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute top-[35%] left-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-8">
            <h2
              className={cx(
                "text-center font-light text-white",
                isMobile
                  ? "text-4xl leading-tight"
                  : "text-[62px] leading-[120%]",
              )}
            >
              {bannerTitle}
            </h2>
            <ActionLink
              to={ROUTES.CONSULTATION}
              variant="light"
              onHover
              hoverDarkWithBorder
            >
              Ar tu kartu su mumis?
            </ActionLink>
          </div>
        </div>
      )}

      <div className="bg-dark flex w-full flex-col items-center px-4 pt-12.5 pb-10 md:pt-[50px] md:pb-[40px]">
        <div className="flex w-full max-w-[1220px] flex-col items-start gap-12 xl:gap-15">
          <div className="flex w-full flex-col justify-between gap-8 self-stretch xl:flex-row xl:items-center xl:gap-0">
            <img
              src={logoLight}
              className="mx-auto w-[200px] xl:mx-0 xl:w-[287px]"
              alt="Telomera logo"
            />
            {/* Social icons moved to bottom on mobile, but on desktop they stay here maybe? The user said "icons should be at the bottom", so I will move them to the bottom entirely or just on mobile. Let's move them to the bottom for all, or show / hide. Wait, let's keep it simple and move them down. */}
            <div className="hidden xl:flex">
              <SocialIcons />
            </div>
          </div>

          <div className="flex w-full flex-col justify-between gap-12 pb-10 xl:flex-row xl:items-start xl:gap-10">
            <div className="flex w-full flex-col items-center gap-[20px] xl:w-[393px] xl:items-start xl:justify-center">
              <h3 className="text-center text-[24px] leading-[120%] font-light text-white xl:text-left xl:text-[28px]">
                Prisijunk prie New Health Elite<sup>™</sup> bendruomenės
              </h3>
              <form
                onSubmit={formik.handleSubmit}
                className="flex w-full flex-col gap-2"
              >
                <div className="flex w-full flex-col xl:w-[393px]">
                  <div className="relative w-full">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="El. paštas"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="w-full"
                      inputClassName="text-dark focus:ring-grey-100 h-[58px] w-full rounded-[30px] bg-white pl-6 pr-[340px] text-[16px] outline-none focus:ring-2"
                    />

                    <Button
                      type="submit"
                      disabled={isLoading || isEmailEmpty}
                      variant="gradientLight"
                      onHover
                      size="sm"
                      className="absolute top-1/2 right-[6px] h-[46px] -translate-y-1/2 gap-[18px] rounded-full px-[30px] text-base font-normal md:text-base"
                    >
                      {isLoading ? "Kraunama..." : "Prenumeruoti"}
                    </Button>
                  </div>

                  {!isEmailEmpty && (
                    <ConfirmationCheckbox className="text-grey-100 mt-2 text-left" />
                  )}

                  <div className="mt-1 flex flex-col items-center text-xs xl:items-start">
                    {!isEmailEmpty &&
                      formik.touched.email &&
                      formik.errors.email && (
                        <div className="text-red-500">
                          {formik.errors.email}
                        </div>
                      )}
                    {!isEmailEmpty && !formik.errors.email && errorMsg && (
                      <div className="text-red-500">{errorMsg}</div>
                    )}
                    {successMsg && (
                      <div className="text-green-500">{successMsg}</div>
                    )}
                  </div>
                </div>
              </form>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:gap-x-10 md:gap-y-[40px]">
              <ul className="flex flex-col gap-[10px]">
                <li className="mb-1 text-[16px] leading-[130%] font-medium text-white">
                  Narystės
                </li>
                <FooterLink to={`${ROUTES.MEMBERSHIPS}#core`}>CORE</FooterLink>
                <FooterLink to={`${ROUTES.MEMBERSHIPS}#apex`}>APEX</FooterLink>
                <FooterLink to={ROUTES.GIFT_COUPON}>Dovanų kuponas</FooterLink>
              </ul>

              <ul className="flex flex-col gap-[10px]">
                <li className="mb-1 text-[16px] leading-[130%] font-medium text-white">
                  Turinio centras
                </li>
                <FooterLink to={ROUTES.BLOG}>Tinklaraštis</FooterLink>
                <FooterLink to={ROUTES.FAQ}>DUK</FooterLink>
                <FooterLink to={ROUTES.ES_INVESTMENT}>
                  ES investicija
                </FooterLink>
              </ul>

              <ul className="flex flex-col gap-[10px]">
                <li className="mb-1 text-[16px] leading-[130%] font-medium text-white">
                  Apie
                </li>
                <FooterLink to={ROUTES.ABOUT}>Apie mus</FooterLink>
                <FooterLink to={ROUTES.CONTACTS}>Kontaktai</FooterLink>
              </ul>

              <ul className="flex flex-col gap-[10px]">
                <li className="mb-1 text-[16px] leading-[130%] font-medium text-white">
                  Teisinė informacija
                </li>
                <FooterLink to={ROUTES.PRIVACY_POLICY}>
                  Privatumo politika
                </FooterLink>
                <FooterLink to={ROUTES.COOKIES_POLICY}>
                  Slapukų politika
                </FooterLink>
              </ul>
            </div>
          </div>

          <div className="flex w-full flex-col items-start justify-center gap-6 md:items-center xl:hidden">
            <SocialIcons />
          </div>

          <div className="flex w-full flex-col items-start gap-[40px] self-stretch">
            <div className="h-[1px] w-full bg-[#333]"></div>
            <div className="flex w-full flex-row items-center justify-between gap-4 xl:gap-0">
              <div className="text-[14px] leading-[130%] text-[#7A7A7A]">
                {currentYear} © Telomera.lt UAB
              </div>
              <img
                src={cofunded_eu}
                className="w-[167px] text-[#7A7A7A]"
                alt="Co-funded by European Union"
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

const YOUTUBE_URL = "https://youtube.com/@telomera_?si=VU_819SI4M4LfLmL";

const SocialIcons: FC = () => {
  const iconWrapperClasses =
    "group relative isolate flex h-10 w-10 items-center justify-center rounded-md bg-[#2A2A2A] transition-all duration-300 ease-in-out xl:h-11 xl:w-11 before:absolute before:-inset-[1px] before:z-0 before:rounded-[inherit] before:opacity-0 before:transition-opacity before:duration-300 before:ease-in-out before:[background-image:var(--gradient-brand-light)] hover:before:opacity-100";

  return (
    <div className="flex items-center justify-start gap-4">
      <a
        href="https://www.facebook.com/telomerahealthspan"
        target="_blank"
        rel="noopener noreferrer"
        className={iconWrapperClasses}
      >
        <span className="relative z-10 flex h-full w-full items-center justify-center">
          <Facebook width={24} height={24} color="white" />
        </span>
      </a>
      <a
        href={YOUTUBE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={iconWrapperClasses}
      >
        <span className="relative z-10 flex h-full w-full items-center justify-center">
          <Youtube width={24} height={24} color="white" />
        </span>
      </a>
      <a
        href="https://www.instagram.com/telomera_/"
        target="_blank"
        rel="noopener noreferrer"
        className={iconWrapperClasses}
      >
        <span className="relative z-10 flex h-full w-full items-center justify-center">
          <Instagram width={24} height={24} color="white" />
        </span>
      </a>
      <a
        href={YOUTUBE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={iconWrapperClasses}
      >
        <span className="relative z-10 flex h-full w-full items-center justify-center">
          <Spotify width={24} height={24} color="white" />
        </span>
      </a>
      <a
        href={YOUTUBE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={iconWrapperClasses}
      >
        <span className="relative z-10 flex h-full w-full items-center justify-center">
          <Podcast width={24} height={24} color="white" />
        </span>
      </a>
    </div>
  );
};

const FooterLink: FC<{ to: string; children: ReactNode }> = ({
  to,
  children,
}) => (
  <li>
    <NavLink
      to={to}
      className="text-[14px] leading-[130%] font-normal text-[#7A7A7A] transition-colors hover:text-white"
    >
      {children}
    </NavLink>
  </li>
);
